import type { CommissionBatchUpdateItem } from '@/entities/commission/api/commission.api';
import {
  formatCommissionBatchUpdateValidationMessage,
  validateCommissionBatchUpdateFile,
} from '@/pages/settlement-fees/commissionBatchUpdateValidation';
import {
  isCsvFile,
  isSpreadsheetFile,
  parseCsvWithHeaderMap,
  readFileAsText,
  SPREADSHEET_UPLOAD_ACCEPT,
} from '@/shared/utils/csvImport';

/** 레거시 `COMMISSION_BATCH_UPDATE_HEADERS` — CSV 한글 헤더 */
export const COMMISSION_BATCH_HEADER_MAP: Record<string, string> = {
  referenceYearMonth: '년월',
  pharmaCompanyName: '제약사',
  drugName: '제품명',
  drugCode: '제품코드',
  price: '보험가',
  standardCommissionRate: '제약사수수료율',
  standardCommissionNote: '비고1(제약사 참고사항)',
  outOfStock: '품절여부',
  outOfStockFrom: '품절(From)',
  outOfStockTo: '품절(To)',
  incentive: '인센티브',
  incentiveNote: '인센티브메모',
};

const REQUIRED_KEYS = [
  'referenceYearMonth',
  'drugCode',
  'price',
  'standardCommissionRate',
] as const;

const normalizeYearMonth = (value: string) => value.replace(/\D/g, '');

const parsePrice = (value: string): number | null => {
  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

/** "12%" 또는 "0.12" 형태의 수수료율을 소수로 변환 */
export const parseCommissionRate = (value: string): number | null => {
  const text = String(value).trim();
  if (!text) return null;
  if (text.endsWith('%')) {
    const num = Number(text.replace('%', '').trim());
    return Number.isFinite(num) ? num / 100 : null;
  }
  const num = Number(text.replace(/,/g, ''));
  if (!Number.isFinite(num)) return null;
  return num > 1 ? num / 100 : num;
};

const parseOutOfStock = (value: string) => {
  if (!value.trim()) return false;
  return value.trim() === '1' || value.trim().toLowerCase() === 'true';
};

const parseApiDate = (value: string): string | undefined | null => {
  const text = value.trim();
  if (!text) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{8}$/.test(text)) {
    return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  }
  return null;
};

export const mapCsvRowsToCommissionBatchItems = (
  rows: Record<string, string>[],
): CommissionBatchUpdateItem[] => {
  const items: CommissionBatchUpdateItem[] = [];

  rows.forEach((row, index) => {
    const rowNum = index + 2;
    const missing = REQUIRED_KEYS.filter((key) => !String(row[key] ?? '').trim());
    if (missing.length > 0) {
      throw new Error(
        `${rowNum}행: 필수 값 누락 (${missing.map((k) => COMMISSION_BATCH_HEADER_MAP[k]).join(', ')})`,
      );
    }

    const referenceYearMonth = normalizeYearMonth(row.referenceYearMonth);
    if (referenceYearMonth.length !== 6) {
      throw new Error(`${rowNum}행: 년월 형식이 올바르지 않습니다.`);
    }

    const price = parsePrice(row.price);
    if (price == null) {
      throw new Error(`${rowNum}행: 보험가가 숫자가 아닙니다.`);
    }

    const standardCommissionRate = parseCommissionRate(row.standardCommissionRate);
    if (standardCommissionRate == null) {
      throw new Error(`${rowNum}행: 제약사수수료율이 올바르지 않습니다.`);
    }

    const outOfStockFrom = row.outOfStockFrom
      ? parseApiDate(row.outOfStockFrom)
      : undefined;
    if (outOfStockFrom === null) {
      throw new Error(`${rowNum}행: 품절(From) 날짜 형식이 올바르지 않습니다.`);
    }

    const outOfStockTo = row.outOfStockTo
      ? parseApiDate(row.outOfStockTo)
      : undefined;
    if (outOfStockTo === null) {
      throw new Error(`${rowNum}행: 품절(To) 날짜 형식이 올바르지 않습니다.`);
    }

    items.push({
      referenceYearMonth,
      pharmaCompanyName: row.pharmaCompanyName || undefined,
      drugName: row.drugName || undefined,
      drugCode: row.drugCode,
      price,
      standardCommissionRate,
      standardCommissionNote: row.standardCommissionNote || undefined,
      incentive: row.incentive || undefined,
      incentiveNote: row.incentiveNote || undefined,
      outOfStock: parseOutOfStock(row.outOfStock ?? ''),
      outOfStockFrom,
      outOfStockTo,
    });
  });

  if (items.length === 0) {
    throw new Error('업로드할 데이터 행이 없습니다.');
  }

  return items;
};

/** 수수료 배치 수정 — CSV·엑셀 업로드 input accept */
export const COMMISSION_BATCH_UPLOAD_ACCEPT = SPREADSHEET_UPLOAD_ACCEPT;

/** 수수료 배치 수정 파일(CSV·엑셀)을 검증 후 항목 배열로 변환 */
export const parseCommissionBatchUploadFile = async (
  file: File,
): Promise<CommissionBatchUpdateItem[]> => {
  if (isSpreadsheetFile(file) && !isCsvFile(file)) {
    const validation = await validateCommissionBatchUpdateFile(file);
    if (!validation.valid) {
      throw new Error(formatCommissionBatchUpdateValidationMessage(validation));
    }
    return validation.items;
  }

  const text = await readFileAsText(file);
  const rows = parseCsvWithHeaderMap(text, COMMISSION_BATCH_HEADER_MAP);
  return mapCsvRowsToCommissionBatchItems(rows);
};
